import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock } from 'lucide-react';
import Error from '@/components/common/Error';
import {
  getDateWithTimeZone,
  getTimeWithTimeZone
} from '@/helpers/dateHelpers';
import { currencyFormat } from '@/helpers/stringHelpers';
import { Button } from '@/components/ui/button';
import { useCart, useDeleteItemFromCart } from '@/data/cart';
import { useInvalidateAvailability } from '@/data/availability';

const CartItem = ({ cartItem, isOrder }) => {
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const cart = useCart(cartItem.cartId);
  const deleteItemFromCart = useDeleteItemFromCart();
  const invalidateAll = useInvalidateAvailability();

  const handleRemove = async () => {
    try {
      setSubmitting(true);
      await deleteItemFromCart(cartItem.cartId, cartItem.id);
      await cart.mutate();
      await invalidateAll();
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pb-4 border-b last:border-b-0">
      <div className="flex justify-between items-center">
        <div className="font-medium">{cartItem.label}</div>
        {!isOrder && (
          <Button
            className="text-xs underline text-muted-foreground px-0"
            variant="ghost"
            onClick={handleRemove}
            disabled={submitting}
          >
            remove
          </Button>
        )}
        {isOrder && cartItem.booking?.id && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleRemove}
            disabled={submitting}
            asChild
          >
            <Link to={`/booking/${cartItem.booking.id}/client`}>
              View Booking
            </Link>
          </Button>
        )}
      </div>
      {apiError && <Error error={apiError} />}
      <div className="text-sm text-muted-foreground flex items-center gap-1">
        <span>
          {cartItem.coach?.user.firstname} {cartItem.coach?.user.lastname}
        </span>
      </div>
      <div className="text-sm text-muted-foreground flex items-center gap-1">
        <span>
          {cartItem.subLabel}
          {cartItem.locationName && ` - ${cartItem.locationName}`}
        </span>
      </div>
      <div className="text-sm mt-2 flex items-center gap-1">
        <Calendar className="h-4 w-4" />
        <span>
          {getDateWithTimeZone(
            cartItem.startDateTime,
            cartItem.timeZone,
            'EEE MMM d, yyyy'
          )}
        </span>
      </div>
      <div className="text-sm mt-1 flex items-center gap-1">
        <Clock className="h-4 w-4" />
        <span>
          {getTimeWithTimeZone(cartItem.startDateTime, cartItem.timeZone)} -{' '}
          {getTimeWithTimeZone(cartItem.endDateTime, cartItem.timeZone)}
        </span>
      </div>
      <div className="flex justify-between items-center mt-2">
        <span className="text-sm text-muted-foreground">
          {currencyFormat(cartItem.price)} x {cartItem.quantity}
        </span>
        <span className="font-medium">{currencyFormat(cartItem.subtotal)}</span>
      </div>
    </div>
  );
};

export default CartItem;
